import { useEffect, useState } from "react";
import { Table, Button, Tag, Space, Typography, Modal, Form, Input, DatePicker, Select, Alert } from "antd";
import { PlusOutlined, RocketOutlined } from "@ant-design/icons";
import { apiRequest } from "@vojomo-core/shared";
import { useNavigate } from "react-router-dom";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";

const { Title } = Typography;

interface Workflow {
  _id: string;
  name: string;
  status: "draft" | "active" | "completed";
  total_participants: number;
  completed_count: number;
  deadline?: string;
  created_at: string;
}

interface PlatformUser {
  _id: string;
  name: string;
  email: string;
}

const STATUS_COLORS: Record<string, string> = {
  draft: "default",
  active: "blue",
  completed: "green"
};

const columns = (
  navigate: ReturnType<typeof useNavigate>
): ColumnsType<Workflow> => [
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    render: (name: string, record: Workflow) => (
      <a onClick={() => navigate(`/coordinator/workflows/${record._id}`)}>
        {name}
      </a>
    )
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status: string) => (
      <Tag color={STATUS_COLORS[status] ?? "default"}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Tag>
    )
  },
  {
    title: "Progress",
    key: "progress",
    render: (_: unknown, record: Workflow) =>
      record.total_participants > 0
        ? `${record.completed_count} / ${record.total_participants}`
        : "—"
  },
  {
    title: "Deadline",
    dataIndex: "deadline",
    key: "deadline",
    render: (deadline?: string) =>
      deadline ? new Date(deadline).toLocaleDateString() : "None"
  },
  {
    title: "Created",
    dataIndex: "created_at",
    key: "created_at",
    render: (d: string) => new Date(d).toLocaleDateString()
  },
  {
    title: "",
    key: "actions",
    render: (_: unknown, record: Workflow) => (
      <Button
        size="small"
        onClick={() => navigate(`/coordinator/workflows/${record._id}`)}
      >
        View
      </Button>
    )
  }
];

export function CoordinatorList(): React.ReactElement {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [kickoffOpen, setKickoffOpen] = useState(false);
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [kickoffForm] = Form.useForm();
  const [isKickingOff, setIsKickingOff] = useState(false);
  const [kickoffError, setKickoffError] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadWorkflows = () => {
    apiRequest<Workflow[]>({ subRoute: "workflow", path: "/list" })
      .then(setWorkflows)
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadWorkflows();
  }, []);

  const openKickoff = () => {
    if (users.length === 0) {
      apiRequest<PlatformUser[]>({ subRoute: "company_admin", path: "/users" }).then(setUsers);
    }
    setKickoffError(null);
    kickoffForm.resetFields();
    setKickoffOpen(true);
  };

  const handleKickoff = async () => {
    try {
      await kickoffForm.validateFields();
    } catch {
      return;
    }
    setIsKickingOff(true);
    setKickoffError(null);
    const values = kickoffForm.getFieldsValue(true);
    try {
      const res = await apiRequest<{ workflow_id: string; sent_count: number }>({
        method: "POST",
        subRoute: "workflow",
        path: "/qbr_kickoff",
        body: {
          qbr_period: values.qbr_period,
          participant_ids: values.participant_ids,
          deadline: values.deadline ? values.deadline.toISOString() : null,
        }
      });
      setKickoffOpen(false);
      loadWorkflows();
      navigate(`/coordinator/workflows/${res.workflow_id}`);
    } catch {
      setKickoffError("Failed to launch QBR round. Please try again.");
    } finally {
      setIsKickingOff(false);
    }
  };

  return (
    <div style={{ padding: "24px 0" }}>
      <Space
        style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Workflows
        </Title>
        <Space>
          <Button
            icon={<RocketOutlined />}
            onClick={openKickoff}
          >
            Launch QBR Round
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/coordinator/create")}
          >
            New Workflow
          </Button>
        </Space>
      </Space>

      <Table
        rowKey="_id"
        loading={isLoading}
        columns={columns(navigate)}
        dataSource={workflows}
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: "No workflows yet. Create one to get started." }}
      />

      <Modal
        title="Launch QBR Round"
        open={kickoffOpen}
        onCancel={() => setKickoffOpen(false)}
        onOk={handleKickoff}
        okText="Launch & Send Now"
        confirmLoading={isKickingOff}
        width={520}
      >
        <p style={{ color: "#666", marginBottom: 20 }}>
          Creates a QBR Scoring workflow and immediately sends it to all selected
          participants via email and in-app notification.
        </p>

        {kickoffError && (
          <Alert type="error" message={kickoffError} style={{ marginBottom: 16 }} closable />
        )}

        <Form form={kickoffForm} layout="vertical">
          <Form.Item
            name="qbr_period"
            label="QBR Period"
            rules={[{ required: true, message: "Enter the QBR period, e.g. Q2 2026" }]}
          >
            <Input placeholder="e.g. Q2 2026" />
          </Form.Item>

          <Form.Item name="deadline" label="Submission deadline (optional)">
            <DatePicker
              style={{ width: "100%" }}
              disabledDate={(d) => d && d < dayjs().startOf("day")}
            />
          </Form.Item>

          <Form.Item
            name="participant_ids"
            label="Participants"
            rules={[{ required: true, message: "Select at least one participant" }]}
          >
            <Select
              mode="multiple"
              placeholder="Search by name or email…"
              optionFilterProp="label"
              options={users.map((u) => ({
                label: `${u.name} (${u.email})`,
                value: u._id
              }))}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
